import { defineAsyncComponent, reactive, toRaw } from "vue";
import { Button, Modal } from "ant-design-vue";
import store from "../../../../../store";
import { addModal } from "../../../../../app.logic";
import i18n from "../../../../../i18n";
import { createColumnInput } from "../../../../../components/table/c-table.logic";
import { getLabTypeByKey, getLabTypeKeyByRoleMenuAttributes } from "../lab-share.logic";
import { createLoading } from "../../../../../utils/loading";
import { required, validateError } from "../../../../../utils/validate";

import { createManageSelect, createPage } from "../../../../../utils/page";
import lodash from "lodash";
import {
    createFieldInput,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { modalStyleForm } from "../../../../../utils/constant";
import { has } from "../../../../../utils/menu-context";
import { filter } from "../../../../../utils/search";
import { openModalList as openModalListTopic } from "../topic/topic-list.logic";
import { onIFrameClickSelectAll } from "../../../../../utils/iframe";
import { openModalForm as openModalFormOperator} from '../operator-form/operator-form.logic';

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/labProductGroup";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const callSaveStandardGroupTopic = ({ form }) => store.dispatch(`resource/v1/master/labCountryStandardGroupTopic/save`, { data: form });
export const callSaveGroupTopicLabTopic = ({ form }) => store.dispatch(`resource/v1/master/labCountryStandardGroupTopicLabTopic/save`, { data: form });
export const callSaveAllGroupTopicLabTopic = ({ data }) => store.dispatch(`resource/v1/master/labCountryStandardGroupTopicLabTopic/saveAll`, { data });
export const callArchiveGroupTopicLabTopic = ({ id }) => store.dispatch(`resource/v1/master/labCountryStandardGroupTopicLabTopic/delete`, { id });
export const callArchiveAllGroupTopicLabTopic = ({ ids }) => store.dispatch(`resource/v1/master/labCountryStandardGroupTopicLabTopic/deleteAll`, { ids });

const CIcon = defineAsyncComponent(() => import('../../../../../components/icon/CIcon'))
const OperatorForm = defineAsyncComponent(() => import('../operator-form/OperatorForm.vue'))
const CopyForm = defineAsyncComponent(() => import('./CopyForm'))


export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onSearch = ({ page, data, topics }) => {
    setIsLoading(true);
    const { where = [] } = page;

    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            size: 1000,
            select: ["**"],
            where: [
                { name: "isActive", type: "Boolean", operator: '=', value: true },
                { name: "refLabType.key", type: "String", operator: '=', value: data.labType?.key },
                { name: "refProductType.id", type: "UUID", operator: '=', value: data.productType?.id },
                ...where,
            ],
            data: [],
            isDistinct: true,
            children: [
                {
                    name: "productGroupStandards",
                    select: ['**'],
                    page: 0,
                    size: 10000,
                    where: [
                        { name: "isActive", type: "Boolean", operator: '=', value: true },
                        { name: "refLabCountryStandardGroup.refLabType.key", type: "String", operator: '=', value: data.labType?.key },
                        { name: "refLabCountryStandardGroup.id", type: "UUID", operator: '=', value: data.labCountryStandardGroup?.id },
                    ],
                    children: [
                        {
                            name: "topics",
                            select: ['**', 'refLabCountryStandardGroupTopic.**', 'refLabTopic.**'],
                            page: 0,
                            size: 10000,
                            where: [
                                { name: "isActive", type: "Boolean", operator: '=', value: true },
                                { name: "refLabTopic.refLabType.key", type: "String", operator: '=', value: data.labType?.key },
                            ],
                            sort: [{ name: "refLabTopic.nameS", type: "ASC" }],
                        }
                    ]
                }
            ]
        }
    }).then(page => {
        state.page = { ...page, where: where }
        data.productGroupStandards = page?.data?.flatMap(d=>d.productGroupStandards) || []
        data.groupStandardTopics = data.productGroupStandards?.flatMap(d=>d.topics) || []
        if (!topics) {
            data.topics = data.groupStandardTopics?.map(d=>d.refLabTopic) || []
            data.topics.forEach(d=>d._id=d.id)
            data.topics = lodash.sortBy(lodash.uniqBy(data.topics, "id"), ["nameS"])
        } else {
            data.topics = topics
        }
        computeTopicColumn({ data })
    })
    .finally(() => setIsLoading(false));
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("model.labProductGroup.form.name"),
            dataIndex: "name",
        }),
    ];
    return columns;
};

export const createForm = ({ data }) => {
    return {
        labType: data.labType,
        productType: data.productType,
        labCountryStandardGroup: data.labCountryStandardGroup,
        topics: [],
    }
}

export const createRules = () => {
    return {
        [`labType`]: [],
        [`productType`]: [],
        [`labCountryStandardGroup`]: [],
    }
}

export const createField = ({ data }) => {
    const { useForm } = data
    const { modelRef } = useForm
    modelRef.labType = data.labType
    return [
        createFieldSelect({
            key: 'labType',
            colProp: () => ({ xs: 24, md: 18, lg: 16 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12, lg: 10 }, wrapperCol: { xs: 24, md: 12, lg: 14 } }),
            label: () => i18n.global.t('model.lookup.modelGroup.LAB_TYPE'),
            inputProp: () => ({
                value: data.labType,
                'onUpdate:value': value => {
                    modelRef.labType = value
                    data.labType = value
                    modelRef.productType = null
                    data.productType = null
                    modelRef.labCountryStandardGroup = null
                    data.labCountryStandardGroup = null
                    if (data.labType && data.productType && data.labCountryStandardGroup) {
                        onSearch({ page: state.page, data })
                    }
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'LAB_TYPE',
                    value: searchQuery,
                    key: data.labAttributes, keyOperator: 'in', valueLogic: 'AND'
                }),
                disabled: data.labAttributes?.length === 1,
                optLabel: "value",
                allowClear: false
            }),
        }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => { }
        },
        createFieldSelect({
            visible: () => data.labType,
            key: 'productType',
            colProp: () => ({ xs: 24, md: 18, lg: 16 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12, lg: 10 }, wrapperCol: { xs: 24, md: 12, lg: 14 } }),
            label: () => i18n.global.t('model.lookup.modelGroup.PRODUCT_TYPE'),
            inputProp: () => ({
                value: data.productType,
                'onUpdate:value': value => {
                    modelRef.productType = value
                    data.productType = value
                    modelRef.labCountryStandardGroup = null
                    data.labCountryStandardGroup = null
                    if (data.labType && data.productType && data.labCountryStandardGroup) {
                        onSearch({ page: state.page, data })
                    }
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', {
                    group: 'PRODUCT_TYPE',
                    value: searchQuery,
                }),
                renderLabel: ({ item }) => <div>{item.valueEn} - {item.value}</div>,
                allowClear: false
            }),
        }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => { }
        },
        createFieldSelect({
            visible: () => data.labType && data.productType,
            key: 'labCountryStandardGroup',
            colProp: () => ({ xs: 24, md: 18, lg: 16 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12, lg: 10 }, wrapperCol: { xs: 24, md: 12, lg: 14 } }),
            label: () => i18n.global.t('model.labCountryStandardGroup.modelName'),
            inputProp: () => ({
                value: data.labCountryStandardGroup,
                'onUpdate:value': value => {
                    modelRef.labCountryStandardGroup = value
                    data.labCountryStandardGroup = value
                    if (data.labType && data.productType && data.labCountryStandardGroup) {
                        onSearch({ page: state.page, data })
                    }
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/labCountryStandardGroup/cacheFindAll', {
                    name: searchQuery,
                    where: [
                        { name: "refLabType.key", type: "String", operator: '=', value: data.labType?.key },
                        { name: "refProductType.id", type: "UUID", operator: '=', value: data.productType?.id },
                    ],
                    options: { force: true }
                }),
                renderLabel: ({ item }) => <div>{item.name}</div>,
                allowClear: false
            }),
        }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => { }
        },
        {
            key: 'topics',
            visible: () => data.canEdit && data.labType && data.productType && data.labCountryStandardGroup,
            colProp: () => ({ xs: 24, md: 18, lg: 16 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12, lg: 10 }, wrapperCol: { xs: 24, md: 12, lg: 14 } }),
            label: () => i18n.global.t('lab.labTopic'),
            inputRender: () => {
                const placeholder = i18n.global.t('button.select') + i18n.global.t('lab.labTopic')
                const onClick = () => openModalListTopic({
                    onIframeMounted: ({ refs, options }) => {
                        refs.iframe.contentWindow.onMounted = ({ refs }) => {
                            refs.table.data.rowSelection.selectedRowKeys.push(...data.topics.map(d=>d.id))
                            refs.table.data.rowSelection.selectedRows.push(...data.topics)
                        }
                        return onIFrameClickSelectAll({
                            refs,
                            options,
                            items: data.topics,
                            itemKey: 'id',
                            rowKey: 'id',
                            afterSelect: ({ rowSelection }) => {
                                const topics = lodash.cloneDeep(data.topics)
                                for (let i = topics.length - 1; i >= 0; i--) {
                                    const isExist = rowSelection.selectedRows.find(d=>d.id === data.topics[i].id)
                                    if (!isExist) {
                                        data.topics.splice(i, 1)
                                    }
                                }
                                data.topics.forEach(d=>{
                                    d.isSave = false
                                    d.isDelete = false
                                })
                                modelRef.topics = data.topics
                                computeTopicColumn({ data })
                            },
                            onSearch: ({ whereAll }) => {
                                whereAll.push({ name: "refLabType.key", type: 'String', operator: '=', value: data?.labType?.key })
                            },
                        })
                    },
                    disabledSearch: ['refParent.id']
                })
                return <Button class="btn-primary" size="small" onClick={onClick}>{placeholder}</Button>
            },
        },
        createFieldSelect({
            visible: () => false,
            key: `topics`,
            colProp: () => ({ xs: 24, md: 18, lg: 16 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 12, lg: 10 }, wrapperCol: { xs: 24, md: 12, lg: 14 } }),
            label: () => i18n.global.t('lab.labTopic'),
            inputProp: () => ({
                value: data.topics,
                optLabel: "name",
                'onUpdate:value': value => {
                    const val = value.filter(d=>!d.isDelete)
                    modelRef.topics = val
                    data.topics = val
                    computeTopicColumn({ data })
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/labTopic/cacheFindAll', { searchQuery, labTypeKey: data.labType?.key }),
                mode: 'multiple',
            }),
        })
    ]
}

const createStandardRecord = ({ labProductGroup, labTopic, data }) => {
    let standardRecord = data.groupStandardTopics.find(d => d.refLabTopic?.id === labTopic.id && d.refLabCountryStandardGroupTopic?.refLabProductGroup?.id === labProductGroup.id)
    if (!standardRecord) {
        let refLabCountryStandardGroupTopic = data.productGroupStandards.find(d=>d.refLabProductGroup?.id === labProductGroup?.id)
        if (!refLabCountryStandardGroupTopic) {
            refLabCountryStandardGroupTopic = {
                isSave: true,
                refProductType: {...data.productType, isSave: false, isDelete: false},
                refLabCountryStandardGroup: {...data.labCountryStandardGroup, isSave: false, isDelete: false},
                refLabType: {...data.labType, isSave: false, isDelete: false},
                refLabProductGroup: {...labProductGroup, isSave: false, isDelete: false},
            }
        }
        standardRecord = reactive({
            refLabCountryStandardGroupTopic: refLabCountryStandardGroupTopic,
            refLabTopic: labTopic,
            sub: labTopic.sub,
            isManualStd: true,
        })
        if (labTopic.refLabFormula?.key === "n c m M") {
            standardRecord.n = labTopic.sub
        }
    }
    return standardRecord
}

const computeTopicColumn = ({ data }) => {
    data.topics.forEach(topic => {
        const labTopic = {...lodash.cloneDeep(topic), isSave: false, isDelete: false}
        const index = state.columns?.findIndex(column => column.dataIndex === labTopic.id )
        if (index === -1) {
            let title = labTopic.nameS
            if (labTopic.refLabTopicUnit?.value) {
                title += ` (${labTopic.refLabTopicUnit?.value})`
            }
            state.columns.push(
                createColumnInput({
                    title: () => title,
                    dataIndex: labTopic.id,
                    isLabTopic: true,
                    sorter: false,
                    width: 180,
                    search: { visible: () => false },
                    align: "center",
                    customRender: ({ record }) => {
                        const labProductGroup = record
                        const standardRecord = createStandardRecord({ labProductGroup, labTopic, data })
                        const item = reactive(lodash.cloneDeep({...standardRecord, isManualStd: true}))
                        const btnJsx = !data.canEdit ? <span></span> : <div className="text-center mt-2">
                            <Button class={`ml-auto ${!item.id ? "btn-success" : "btn-warning"}`} size="small"
                                    onClick={ () =>
                                        {
                                            const callBack = () => {
                                                setIsLoading(true);
                                                console.log('callSaveGroupTopicLabTopic', item)
                                                callSaveGroupTopicLabTopic({ form: item }).finally(() => {
                                                    setIsLoading(false);
                                                    onSearch({ page: state.page, data, topics: data.topics })
                                                });
                                            }
                                            openModalFormOperator({ props: { viewOnly: false, form: item, visibleTopic: true }, callBack })
                                        }
                                    }>
                                <CIcon file={!item.id ? "add-curve.svg" : "edit.svg"}></CIcon>
                            </Button>
                            <Button class="ml-auto btn-danger" size="small" disabled={!item.id} onClick={() => {
                                Modal.confirm({
                                    title: i18n.global.t('components.table.operatorAndPagination.youAreWant', { text: i18n.global.t('button.delete') }),
                                    autoFocusButton: 'cancel',
                                    okType: 'danger',
                                    onOk: () => {
                                        setIsLoading(true)
                                        callArchiveGroupTopicLabTopic({ id: item.id }).finally(() => {
                                            setIsLoading(false)
                                            onSearch({ page: state.page, data, topics: data.topics })
                                        })
                                    },
                                });
                            }}>
                                <CIcon file="delete.svg"></CIcon>
                            </Button>
                        </div>
                        return <div><OperatorForm form={standardRecord}></OperatorForm>{btnJsx}</div>
                    },
                }),
            )
        }
    })
    const columns = lodash.cloneDeep(state.columns)
    for (let columnIndex = columns.length - 1; columnIndex >= 0; columnIndex--) {
        const column = columns[columnIndex]
        if (column.isLabTopic) {
            const index = data.topics?.findIndex(labTopic => column.dataIndex === labTopic.id )
            if (index === -1) { state.columns.splice(columnIndex, 1) }
        }
    }
}

// export const openModalOperatorForm = ({ record, data }) => {
//     const options = addModal({
//         bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
//             width: "40%",
//             okText: i18n.global.t('button.save'),
//             onOk: () => {
//                 setIsLoading(true);
//                 options.bind.onCancel()
//                 callSaveGroupTopicLabTopic({ form: record }).finally(() => {
//                     setIsLoading(false);
//                     onSearch({ page: state.page, data, topics: data.topics })
//                 });
//             }
//         }),
//         render: () => <OperatorForm form={ record } viewOnly={false}></OperatorForm>
//     })
//     return options
// }

export const createFieldCopy = ({ data, record }) => {
    const { modelRef } = data.copyFormData.useForm
    return [
        createFieldInput({
            key: "from",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 8 }, wrapperCol: { xs: 16 } }),
            label: () => i18n.global.t("button.copyFrom"),
            inputProp: () => ({ value: modelRef.from?.name, disabled: true })
        }),
        createFieldSelect({
            key: `labProductGroups`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 8 }, wrapperCol: { xs: 16 } }),
            label: () => i18n.global.t('button.copyTo'),
            inputProp: () => ({
                value: modelRef.labProductGroups,
                optLabel: "name",
                'onUpdate:value': value => { modelRef.labProductGroups = value },
                list: ({ searchQuery }) => filter({
                    list: state.page?.data || [],
                    where: [
                        { name: "id", type: "UUID", isNot: true, operator: "=", value: record?.id },
                        { name: 'name', type: 'String', operator: 'like', value: searchQuery }
                    ],
                    sort: [{ name: 'name', type: 'ASC' }]
                }),
                mode: "multiple"
            }),
        })
    ]
}

export const openModalCopyForm = ({ record, data }) => {
    data.copyFormData = {}
    data.copyFormData.form = { from: record, labProductGroups: [] }
    data.copyFormData.rules = { labProductGroups: [required]}
    data.copyFormData.useForm = createUseForm(data.copyFormData.form, data.copyFormData.rules)
    data.copyFormData.fields = createFieldCopy({ data: data, record })
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            width: "600px",
            okText: i18n.global.t('button.save'),
            onOk: () => {
                data.copyFormData.useForm.validate()
                    .then(() => {
                        Modal.confirm({
                            title: i18n.global.t('components.table.operatorAndPagination.youAreWant', { text: i18n.global.t('button.copy') }),
                            onOk: () => {
                                const copyFrom = record.productGroupStandards?.flatMap(d=>d.topics)
                                const labProductGroups = toRaw(data.copyFormData.useForm.modelRef)?.labProductGroups
                                labProductGroups?.forEach(labProductGroup => {
                                    const copyTo = labProductGroup?.productGroupStandards?.flatMap(d=>d.topics)
                                    const idsForArchive = []
                                    const dataForSave = []
                                    data.topics.forEach(labTopic => {
                                        const topicCopyTo = copyTo.find(topicCopyFrom => topicCopyFrom.refLabTopic?.id === labTopic.id)
                                        const topicCopyFrom = copyFrom.find(topicCopyFrom => topicCopyFrom.refLabTopic?.id === labTopic.id)
                                        if (topicCopyTo || topicCopyFrom) {
                                            if (!topicCopyFrom && topicCopyTo) {
                                                idsForArchive.push(topicCopyTo.id)
                                            } else {
                                                const standardRecord = lodash.cloneDeep(createStandardRecord({ labProductGroup, labTopic, data }))
                                                const topicCopyFromClone = lodash.cloneDeep(topicCopyFrom)
                                                const form = {
                                                    ...topicCopyFromClone,
                                                    id: standardRecord.id,
                                                    version: standardRecord.version,
                                                    refLabCountryStandardGroupTopic: standardRecord.refLabCountryStandardGroupTopic,
                                                    refLabTopic: standardRecord.refLabTopic,
                                                }
                                                dataForSave.push(form);
                                            }
                                        }
                                    })

                                    setIsLoading(true)
                                    callArchiveAllGroupTopicLabTopic({ ids: idsForArchive }).finally(() => {
                                        const parent = dataForSave.find(d=>!d.refLabCountryStandardGroupTopic?.id)
                                        if (parent) {
                                            callSaveStandardGroupTopic({ form: parent.refLabCountryStandardGroupTopic }).then((refLabCountryStandardGroupTopic) => {
                                                dataForSave.map(d=>d.refLabCountryStandardGroupTopic = refLabCountryStandardGroupTopic)
                                                callSaveAllGroupTopicLabTopic({ data: dataForSave }).finally(() => {
                                                    setIsLoading(false)
                                                    onSearch({ page: state.page, data, topics: data.topics })
                                                })
                                            })
                                        } else {
                                            callSaveAllGroupTopicLabTopic({ data: dataForSave }).finally(() => {
                                                setIsLoading(false)
                                                onSearch({ page: state.page, data, topics: data.topics })
                                            })
                                        }
                                    })
                                })
                                options.bind.onCancel()
                            },
                        });
                    })
                    .catch((error) => {
                        validateError({error})
                        throw error
                    });
            }
        }),
        render: () => <CopyForm data={data.copyFormData}></CopyForm>
    })
    return options
}

export const initState = async ({ props, data, refs }) => {
    const menuContext = props.menuContext;
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    const labTypeKeys = await getLabTypeByKey({ searchQuery: data.labAttributes[0] });
    data.labType = labTypeKeys[0];
    data.topics = []
    data.form = createForm({ props, data, refs, isLoading });
    data.rules = createRules({ props, data, refs, isLoading });
    data.useForm =  createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.canEdit = has({ menuContext, key: "canEdit" })

    state.columns = createColumns({ props, data });
    state.manages = [
        createManageSelect({ menuContext }),
        {
            icon: () => <CIcon file="select.svg"></CIcon>,
            text: () => i18n.global.t("button.copy"),
            visible: () => data.canEdit,
            rowSelection: true,
            buttonProp: { class: "btn-warning" },
            type: "radio",
            onClick: ({ rowSelection }) => {
                const record = rowSelection?.selectedRows[0]
                if (record) { openModalCopyForm({ record, data }) }
            }
        },
    ];
    state.manageExtras = [];
    state.rowActions = [];
};

export const state = reactive({
    columns: [],
    page: createPage({
        options: {
            select: ["**"]
        }
    }),
    tableOptions: { search: { activeKey: null } }
});
